import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { BusinessGreeting } from "@/components/dashboard/BusinessGreeting";
import { BusinessPulse } from "@/components/dashboard/BusinessPulse";
import { NeedsYou } from "@/components/dashboard/NeedsYou";
import { RevenueOpportunities } from "@/components/dashboard/RevenueOpportunities";
import { BusinessToday } from "@/components/dashboard/BusinessToday";
import { MoneyMap } from "@/components/dashboard/MoneyMap";
import { CustomerSignals } from "@/components/dashboard/CustomerSignals";
import { LiveActivity } from "@/components/dashboard/LiveActivity";
import { ShallyBriefing } from "@/components/dashboard/ShallyBriefing";
import { BusinessActionsCard } from "@/components/dashboard/BusinessActionsCard";
import { SinceLastVisit } from "@/components/dashboard/SinceLastVisit";
import { MoneyIntel } from "@/components/dashboard/MoneyIntel";
import { OutstandingCustomers } from "@/components/dashboard/OutstandingCustomers";
import { UrgentCollections } from "@/components/dashboard/UrgentCollections";
import { DashboardRange } from "@/components/dashboard/DashboardRange";
import { DashboardViewSelector, useDashboardBranch } from "@/components/dashboard/DashboardViewSelector";
import { OrderSoonCard } from "@/components/dashboard/OrderSoonCard";
import { Loading } from "@/components/pawkt/Empty";
import { useAuth } from "@/context/AuthContext";
import { hasPerm, moduleOn } from "@/lib/perms";
import { TrialCountdown } from "@/components/billing/Trial";
import { FollowUpCenter } from "@/components/dashboard/FollowUpCenter";
import { CrossSellCard } from "@/components/dashboard/CrossSellCard";
import { PreventiveCareCard } from "@/components/dashboard/PreventiveCareCard";
import { ServiceProfitabilityCard } from "@/components/dashboard/ServiceProfitabilityCard";
import ReceptionDashboard from "@/pages/ReceptionDashboard";
import { ConnectReceptionistCard } from "@/components/receptionist/ConnectReceptionistCard";
import { AccountsHome, ProcurementHome } from "@/components/accounts/RoleHome";
import { SelfBookingPanel, SelfBookingButton } from "@/components/pawkt/SelfBookingPanel";
import { PendingApprovals } from "@/components/dashboard/PendingApprovals";
import VetHome from "@/pages/VetHome";
import GroomerHome from "@/pages/GroomerHome";
import { ConsultBillingQueue } from "@/components/pawkt/ConsultBillingQueue";
import { NewLeadsCard } from "@/components/pawkt/NewLeadsCard";
import { PawPrint, ArrowRight, Compass } from "lucide-react";

function CommandCenterEntry() {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate("/command-center")} data-testid="dashboard-command-center-entry"
      className="group flex w-full items-center justify-between gap-4 rounded-2xl border border-[#D7C9FB] bg-gradient-to-r from-[#EEE9FE] to-[#FBF5FF] p-5 text-left transition-all hover:shadow-md">
      <div className="flex items-center gap-4">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary text-white shadow-purpleGlow">
          <Compass className="h-5 w-5" />
        </div>
        <div>
          <p className="font-extrabold text-pawkt-text">Open your Command Center</p>
          <p className="text-sm text-pawkt-text-secondary">What needs you, what Pawkt noticed, recommends and already did — in one calm view.</p>
        </div>
      </div>
      <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-bold text-white">
        Go <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </span>
    </button>
  );
}

function ZeroState() {
  const navigate = useNavigate();
  return (
    <div className="rounded-3xl border border-pawkt-border bg-white p-8 sm:p-12 text-center shadow-card" data-testid="dashboard-zero-state">
      <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl pawkt-gradient-primary shadow-purpleGlow">
        <PawPrint className="h-8 w-8 text-white" />
      </div>
      <h2 className="mt-5 font-display text-2xl font-extrabold text-pawkt-text">Your business is just getting started</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-pawkt-text-secondary">
        Once you complete your first booking or transaction, Pawkt Pulse will come alive with your revenue,
        customer activity, bookings, business pulse and return patterns.
      </p>
      <div className="mx-auto mt-5 flex max-w-sm flex-wrap justify-center gap-2 text-xs font-semibold text-pawkt-text-secondary">
        {["Revenue", "Customer activity", "Bookings", "Business Pulse", "Return patterns"].map((t) => (
          <span key={t} className="rounded-full bg-pawkt-elevated px-3 py-1">{t}</span>
        ))}
      </div>
      <button onClick={() => navigate("/bookings")} data-testid="zero-create-booking"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-purpleGlow hover:bg-pawkt-primary-hover">
        Create your first booking <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  // Least-privilege staff (no reports.view: receptionist, vet, groomer, trainer,
  // and custom roles without reporting) land on the operational dashboard. Owner/
  // admin/manager/accountant keep the full dashboard (its /dashboard data is
  // already server-minimized for anyone lacking reports.view).
  if (!hasPerm(user, "reports.view")) {
    // Finance staff (Accounts / Procurement, by permission) get a role dashboard instead of front-desk widgets
    if (hasPerm(user, "payables.view") && !hasPerm(user, "invoices.create")) return <><PendingApprovals />{hasPerm(user, "payables.pay_execute") ? <AccountsHome /> : <ProcurementHome />}</>;
    if (hasPerm(user, "vet.clinical") && moduleOn(user, "vet") && !hasPerm(user, "invoices.create")) return <><PendingApprovals /><VetHome /></>;
    if (hasPerm(user, "grooming.complete") && moduleOn(user, "grooming") && !hasPerm(user, "invoices.create") && !hasPerm(user, "vet.clinical")) return <><PendingApprovals /><GroomerHome /></>;
    if (hasPerm(user, "invoices.create")) return (<><NewLeadsTop reception /><ReceptionDashboard /><OwnerDashboard embedded /></>);
    return (<><NewLeadsTop reception /><ReceptionDashboard /></>);
  }
  return (<><NewLeadsTop /><OwnerDashboard /></>);
}

function NewLeadsTop({ reception = false }) {
  return <><PendingApprovals /><div className="mb-6 empty:hidden"><NewLeadsCard /></div><SelfBookingPanel />{reception && <div className="mb-4"><SelfBookingButton /></div>}</>;
}

function OwnerDashboard({ embedded = false }) {
  const { user } = useAuth();
  const canReports = hasPerm(user, "reports.view");
  const canOps = canReports || hasPerm(user, "invoices.create");
  const showCC = canReports && moduleOn(user, "command_center");
  const [period, setPeriod] = useState("today");
  const view = useDashboardBranch();
  const dash = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });
  const viewBar = !embedded && <DashboardViewSelector branch={view.branch} branches={view.branches} onChange={view.change} />;

  const d = dash.data;
  const isEmpty = d && (d.customers?.total || 0) === 0 && (d.revenue?.year || 0) === 0 &&
    ((d.bookings?.today || 0) + (d.bookings?.upcoming || 0) + (d.bookings?.completed || 0)) === 0;
  const lowData = d && !isEmpty && (d.revenue?.month || 0) === 0 && (d.bookings?.completed || 0) < 3;

  if (dash.isLoading) return <div className="space-y-6">{viewBar}<Loading label={view.branch ? "Loading branch view" : "Loading your business"} /></div>;

  if (isEmpty && !embedded) {
    return (
      <div className="space-y-6" data-testid="pawkt-pulse">
        <TrialCountdown />
        {viewBar}
        <BusinessGreeting period={period} onPeriod={setPeriod} />
        <SelfBookingButton />
        {canReports && <DashboardRange />}
        {showCC && <CommandCenterEntry />}
        {hasPerm(user, "settings.edit") && <ConnectReceptionistCard />}
        {view.branch ? <div className="rounded-2xl border border-dashed border-pawkt-border bg-white p-8 text-center text-sm text-pawkt-text-secondary" data-testid="dash-view-empty">No records for this branch yet. Choose another branch or All Branches.</div> : <ZeroState />}
      </div>
    );
  }

  // Asymmetric grid; DOM order == mobile priority. Spans sum to 3 per row on lg.
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid={embedded ? "reception-ops-dashboard" : "pawkt-pulse"}>
      {!embedded && <div className="lg:col-span-3"><TrialCountdown /></div>}
      {viewBar && <div className="lg:col-span-3">{viewBar}</div>}
      {!embedded && <div className="lg:col-span-3"><BusinessGreeting period={period} onPeriod={setPeriod} /><div className="mt-3"><SelfBookingButton /></div></div>}
      {canReports && <div className="lg:col-span-3"><DashboardRange /></div>}
      {showCC && <div className="lg:col-span-3"><CommandCenterEntry /></div>}
      {(canReports || hasPerm(user, "expenses.view")) && <div className="lg:col-span-3"><BusinessActionsCard /></div>}
      {!embedded && hasPerm(user, "settings.edit") && <div className="lg:col-span-3"><ConnectReceptionistCard /></div>}
      <div className="lg:col-span-3"><NeedsYou canReports={canOps} /></div>
      {!embedded && hasPerm(user, "invoices.create") && moduleOn(user, "vet") && <div className="lg:col-span-3"><ConsultBillingQueue /></div>}
      {(hasPerm(user, "customers.view") || hasPerm(user, "vet.view")) && <div className="lg:col-span-3"><FollowUpCenter /></div>}
      <div className="lg:col-span-3"><BusinessPulse canReports={canReports} lowData={lowData} /></div>

      {hasPerm(user, "invoices.view") && <UrgentCollections />}
      {hasPerm(user, "invoices.view") && <div className="lg:col-span-3"><OutstandingCustomers /></div>}

      <div className="lg:col-span-1"><RevenueOpportunities canReports={canOps} /></div>
      <div className="lg:col-span-2"><BusinessToday /></div>

      {canReports && <div className="lg:col-span-3"><MoneyIntel /></div>}
      {canReports && hasPerm(user, "expenses.view") && <div className="lg:col-span-3"><ServiceProfitabilityCard /></div>}
      <div className="lg:col-span-3"><CrossSellCard /></div>
      {hasPerm(user, "vet.view") && <div className="lg:col-span-3"><PreventiveCareCard /></div>}

      {hasPerm(user, "inventory.view") && <div className="lg:col-span-3"><OrderSoonCard /></div>}

      <div className="lg:col-span-1"><CustomerSignals canReports={canReports} /></div>
      <div className="lg:col-span-2"><MoneyMap /></div>

      <div className="lg:col-span-1"><LiveActivity /></div>
      <div className="lg:col-span-2"><ShallyBriefing canReports={canReports} /></div>

      <div className="lg:col-span-3"><SinceLastVisit canReports={canReports} /></div>
    </div>
  );
}
